import React, { useEffect, useState } from 'react';
import api from '../../../utils/api';
import { useAuth } from '../../../context/AuthContext';
import PortalPageShell, { PortalLoading } from '../PortalPageShell';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { useToast } from '../../../hooks/use-toast';

export default function PortalParentMessages() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [contacts, setContacts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);

  const loadContacts = async () => {
    setLoading(true);
    try {
      const res = await api.get('/chat/contacts', { params: { role: 'parent_portal' } });
      setContacts(res.data?.data?.items ?? []);
    } catch {
      setContacts([]);
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (contact) => {
    if (!contact?.user_id) return;
    try {
      const res = await api.get(`/chat/conversations/with/${contact.user_id}`);
      setMessages(res.data?.data?.messages ?? []);
    } catch {
      setMessages([]);
    }
  };

  useEffect(() => {
    loadContacts();
  }, []);

  useEffect(() => {
    if (selected) loadMessages(selected);
  }, [selected]);

  const send = async (e) => {
    e.preventDefault();
    const msg = text.trim();
    if (!msg || !selected) return;
    try {
      await api.post('/chat/messages', { recipient_user_id: selected.user_id, message: msg });
      setText('');
      loadMessages(selected);
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.message || 'Send failed', variant: 'destructive' });
    }
  };

  return (
    <PortalPageShell title="Messages" description="Chat with teachers and school admin">
      {loading ? <PortalLoading /> : null}
      <div className="grid gap-4 lg:grid-cols-3 min-h-[420px]">
        <Card className="lg:col-span-1">
          <CardContent className="pt-4 space-y-1 max-h-[480px] overflow-y-auto">
            {contacts.map((c) => (
              <button
                key={c.user_id}
                type="button"
                className={`w-full text-left px-3 py-2 rounded-md text-sm ${selected?.user_id === c.user_id ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
                onClick={() => setSelected(c)}
              >
                <div className="font-medium">{c.name}</div>
                <div className="text-xs opacity-80">{c.subtitle}</div>
              </button>
            ))}
            {contacts.length === 0 && !loading ? (
              <p className="text-sm text-muted-foreground">No contacts available.</p>
            ) : null}
          </CardContent>
        </Card>
        <Card className="lg:col-span-2 flex flex-col">
          <CardContent className="pt-4 flex flex-col flex-1 min-h-[400px]">
            {selected ? (
              <>
                <p className="font-medium mb-2">{selected.name}</p>
                <div className="flex-1 overflow-y-auto space-y-2 mb-3 border rounded-md p-3 bg-muted/30">
                  {messages.map((m) => {
                    const mine = Number(m.sender_id) === Number(user?.id);
                    return (
                      <div key={m.id} className={`text-sm ${mine ? 'text-right' : ''}`}>
                        <span className="inline-block rounded px-2 py-1 bg-card border">{m.body || m.message}</span>
                      </div>
                    );
                  })}
                </div>
                <form onSubmit={send} className="flex gap-2">
                  <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message…" />
                  <Button type="submit">Send</Button>
                </form>
              </>
            ) : (
              <p className="text-muted-foreground text-sm">Select a contact to start messaging.</p>
            )}
          </CardContent>
        </Card>
      </div>
    </PortalPageShell>
  );
}
